import { useCallback, useEffect, useRef, useState } from 'react'

import riverSound from '@/assets/audio/atmosphere-of-a-small-river-water-splash.mp3'
import forestSound from '@/assets/audio/birds-chirping-waterfall-in-the-background-forest-atmosphere.mp3'
import gentleSound from '@/assets/audio/water-slowly-flows-out-of-the-hose.mp3'

interface SoundEffect {
  type: 'sine' | 'square' | 'triangle' | 'sawtooth' | 'noise' | 'water'
  frequency: number[]
  duration: number
  volume: number
  envelope: { attack: number; decay: number; sustain: number; release: number }
}

const SOUNDS: Record<string, SoundEffect> = {
  bubble: {
    type: 'sine',
    frequency: [350, 550, 700],
    duration: 0.18,
    volume: 0.3,
    envelope: { attack: 0.01, decay: 0.12, sustain: 0.25, release: 0.05 },
  },
  eat: {
    type: 'sine',
    frequency: [220, 380, 480],
    duration: 0.25,
    volume: 0.4,
    envelope: { attack: 0.01, decay: 0.12, sustain: 0.2, release: 0.12 },
  },
  splash: {
    type: 'water',
    frequency: [200, 800],
    duration: 0.4,
    volume: 0.35,
    envelope: { attack: 0.005, decay: 0.15, sustain: 0.15, release: 0.1 },
  },
  positive: {
    type: 'sine',
    frequency: [523, 659, 784, 1047],
    duration: 0.5,
    volume: 0.35,
    envelope: { attack: 0.05, decay: 0.15, sustain: 0.4, release: 0.3 },
  },
  negative: {
    type: 'triangle',
    frequency: [200, 150],
    duration: 0.3,
    volume: 0.3,
    envelope: { attack: 0.02, decay: 0.2, sustain: 0.2, release: 0.08 },
  },
  coin: {
    type: 'sine',
    frequency: [1000, 1200, 1400, 1600],
    duration: 0.35,
    volume: 0.4,
    envelope: { attack: 0.01, decay: 0.1, sustain: 0.25, release: 0.24 },
  },
}

const AMBIENT_FILE_MAP: Record<string, string> = {
  river: riverSound,
  forest: forestSound,
  gentle: gentleSound,
}

export function useAudio() {
  const [muted, setMuted] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [ambientSoundId, setAmbientSoundId] = useState<string>('none')
  const audioContext = useRef<AudioContext | null>(null)
  const ambientFileSource = useRef<AudioBufferSourceNode | null>(null)
  const ambientFileGain = useRef<GainNode | null>(null)
  const audioBuffers = useRef<Record<string, AudioBuffer | null>>({})
  const ambientRequestId = useRef<number>(0)

  const getContext = useCallback(() => {
    if (!audioContext.current || audioContext.current.state === 'closed') {
      audioContext.current = new AudioContext()
    }
    if (audioContext.current.state === 'suspended') {
      audioContext.current.resume().catch(() => {})
    }
    return audioContext.current
  }, [])

  const unlock = useCallback(async () => {
    if (initialized) return
    if (!audioContext.current || audioContext.current.state === 'closed') {
      audioContext.current = new AudioContext()
    }
    if (audioContext.current.state === 'suspended') {
      await audioContext.current.resume()
    }
    setInitialized(true)
  }, [initialized])

  const loadAudioBuffer = useCallback(async (soundId: string): Promise<AudioBuffer | null> => {
    if (audioBuffers.current[soundId]) {
      return audioBuffers.current[soundId]
    }
    const url = AMBIENT_FILE_MAP[soundId]
    if (!url) return null

    try {
      const response = await fetch(url)
      const arrayBuffer = await response.arrayBuffer()
      const ctx = getContext()
      const buffer = await ctx.decodeAudioData(arrayBuffer)
      audioBuffers.current[soundId] = buffer
      return buffer
    } catch {
      return null
    }
  }, [getContext])

  const playTone = useCallback(
    (type: OscillatorType, frequencies: number[], duration: number, volume: number, envelope: SoundEffect['envelope']) => {
      const ctx = getContext()
      const now = ctx.currentTime

      frequencies.forEach((freq, index) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.type = type
        osc.frequency.setValueAtTime(freq, now + index * 0.04)

        gain.gain.setValueAtTime(0, now + index * 0.04)
        gain.gain.linearRampToValueAtTime(volume, now + index * 0.04 + envelope.attack)
        gain.gain.linearRampToValueAtTime(volume * envelope.sustain, now + index * 0.04 + envelope.attack + envelope.decay)
        gain.gain.linearRampToValueAtTime(0, now + index * 0.04 + duration)

        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.start(now + index * 0.04)
        osc.stop(now + index * 0.04 + duration)
      })
    },
    [getContext],
  )

  const playNoise = useCallback(
    (duration: number, volume: number, envelope: SoundEffect['envelope'], frequencyRange: [number, number]) => {
      const ctx = getContext()
      const now = ctx.currentTime

      const bufferSize = ctx.sampleRate * duration
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
      const data = buffer.getChannelData(0)

      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1
        b0 = 0.99886 * b0 + white * 0.0555179
        b1 = 0.99332 * b1 + white * 0.0750759
        b2 = 0.96900 * b2 + white * 0.1538520
        b3 = 0.90000 * b3 + white * 0.3104856
        b4 = 0.65000 * b4 + white * 0.5329522
        b5 = -0.7616 * b5 - white * 0.0168980
        data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362
        data[i] *= 0.11 * Math.exp(-i / (bufferSize * 0.4))
        b6 = white * 0.115926
      }

      const noise = ctx.createBufferSource()
      noise.buffer = buffer

      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.setValueAtTime(frequencyRange[0] + Math.random() * (frequencyRange[1] - frequencyRange[0]), now)
      filter.Q.setValueAtTime(0.5, now)

      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0, now)
      gain.gain.linearRampToValueAtTime(volume, now + envelope.attack)
      gain.gain.linearRampToValueAtTime(volume * envelope.sustain, now + envelope.attack + envelope.decay)
      gain.gain.linearRampToValueAtTime(0, now + duration)

      noise.connect(filter)
      filter.connect(gain)
      gain.connect(ctx.destination)

      noise.start(now)
      noise.stop(now + duration)
    },
    [getContext],
  )

  const playWaterSplash = useCallback(
    (duration: number, volume: number, envelope: SoundEffect['envelope']) => {
      const ctx = getContext()
      const now = ctx.currentTime

      const bufferSize = ctx.sampleRate * duration
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
      const data = buffer.getChannelData(0)

      for (let i = 0; i < bufferSize; i++) {
        const t = i / bufferSize
        const envelopeVal = t < envelope.attack ? t / envelope.attack :
                           t < envelope.attack + envelope.decay ? 
                             1 - (t - envelope.attack) / envelope.decay * (1 - envelope.sustain) :
                           t < duration - envelope.release ? envelope.sustain :
                             envelope.sustain * (1 - (t - (duration - envelope.release)) / envelope.release)
        
        const noise = (Math.random() * 2 - 1) * envelopeVal
        const wave = Math.sin(t * Math.PI * 8) * 0.3 * envelopeVal
        data[i] = (noise + wave) * volume * (1 - t * 0.5)
      }

      const noise = ctx.createBufferSource()
      noise.buffer = buffer

      const filter = ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.setValueAtTime(1200, now)
      filter.frequency.exponentialRampToValueAtTime(200, now + duration)

      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0, now)
      gain.gain.linearRampToValueAtTime(volume, now + envelope.attack)
      gain.gain.linearRampToValueAtTime(volume * envelope.sustain, now + envelope.attack + envelope.decay)
      gain.gain.linearRampToValueAtTime(0, now + duration)

      noise.connect(filter)
      filter.connect(gain)
      gain.connect(ctx.destination)

      noise.start(now)
      noise.stop(now + duration)
    },
    [getContext],
  )

  const play = useCallback(
    (soundId: string) => {
      if (muted) return

      const sound = SOUNDS[soundId]
      if (!sound) return

      if (sound.type === 'noise') {
        playNoise(sound.duration, sound.volume, sound.envelope, sound.frequency as [number, number])
      } else if (sound.type === 'water') {
        playWaterSplash(sound.duration, sound.volume, sound.envelope)
      } else {
        playTone(sound.type, sound.frequency as number[], sound.duration, sound.volume, sound.envelope)
      }
    },
    [muted, playNoise, playWaterSplash, playTone],
  )

  const stopAmbient = useCallback(() => {
    const ctx = audioContext.current
    if (!ctx || ctx.state === 'closed') return

    const now = ctx.currentTime

    if (ambientFileSource.current) {
      try {
        ambientFileSource.current.stop(now)
      } catch {
      }
      ambientFileSource.current = null
    }
    ambientFileGain.current = null
    ambientRequestId.current += 1
  }, [])

  const playAmbientFile = useCallback(async (soundId: string) => {
    if (muted) return

    const currentRequestId = ++ambientRequestId.current

    const buffer = await loadAudioBuffer(soundId)
    
    if (currentRequestId !== ambientRequestId.current) {
      return
    }

    if (muted || soundId !== ambientSoundId) {
      return
    }

    const ctx = getContext()
    const now = ctx.currentTime

    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.3, now + 2)

    source.connect(gain)
    gain.connect(ctx.destination)

    source.start(now)
    ambientFileSource.current = source
    ambientFileGain.current = gain
  }, [loadAudioBuffer, muted, getContext, ambientSoundId])

  const playAmbient = useCallback(() => {
    stopAmbient()

    if (muted) return

    if (ambientSoundId !== 'none') {
      playAmbientFile(ambientSoundId)
    }
  }, [muted, ambientSoundId, playAmbientFile, stopAmbient])

  useEffect(() => {
    return () => {
      stopAmbient()
      const ctx = audioContext.current
      if (ctx && ctx.state !== 'closed') {
        ctx.close().catch(() => {})
      }
    }
  }, [stopAmbient])

  useEffect(() => {
    if (initialized && !muted) {
      playAmbient()
    } else {
      stopAmbient()
    }
  }, [initialized, muted])

  useEffect(() => {
    if (initialized && !muted) {
      playAmbient()
    } else {
      stopAmbient()
    }
  }, [ambientSoundId])

  return { play, playAmbient, stopAmbient, muted, setMuted, unlock, ambientSoundId, setAmbientSoundId }
}

export type AudioApi = ReturnType<typeof useAudio>
